import React, { useEffect, useState, useCallback } from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './App.css';
import DecaySimulator from './DecaySimulator';
import AlertToast from './AlertToast';
import useSocket from './useSocket';
import BoostModal from './BoostModal';
import LoginPage from './LoginPage';

function App() {
  // Auth state
  const [token, setToken] = useState(() => localStorage.getItem('amr_token') || '');
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('amr_user') || 'null'); } catch { return null; }
  });

  const isAuthenticated = !!token && !!user;

  const handleLogin = useCallback((newToken, newUser) => {
    setToken(newToken);
    setUser(newUser);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('amr_token');
    localStorage.removeItem('amr_user');
    setToken('');
    setUser(null);
    setClients([]);
    setSelectedClient('');
    setData({ history: [], forecast: [], decay: [] });
  }, []);

  // Authenticated fetch helper
  const authFetch = useCallback((url, options = {}) => {
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
      },
    }).then(res => {
      if (res.status === 401) {
        handleLogout();
        throw new Error('Session expired');
      }
      return res;
    });
  }, [token, handleLogout]);

  // State for client list and selected client
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [data, setData] = useState({ history: [], forecast: [], decay: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Collection state
  const [collecting, setCollecting] = useState(false);
  const [, setCollectProgress] = useState('');

  // Per-prompt results state
  const [results, setResults] = useState(null);

  // State for new config form
  const [newClientName, setNewClientName] = useState('');
  const [newKeywords, setNewKeywords] = useState('');
  const [newPlatforms, setNewPlatforms] = useState('');
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  // Boost modal state
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [activeBoost, setActiveBoost] = useState(null);

  // API Keys settings state
  const [showKeySettings, setShowKeySettings] = useState(false);
  const [keyStatus, setKeyStatus] = useState({});
  const [keyInputs, setKeyInputs] = useState({ chatgpt: '', perplexity: '', gemini: '', grok: '' });
  const [keySaving, setKeySaving] = useState(false);
  const [keyMessage, setKeyMessage] = useState('');

  // Real-time socket connection
  const { connected, alerts, dismissAlert } = useSocket();

  // Fetch client list on mount (only when authenticated)
  useEffect(() => {
    if (!isAuthenticated) return;
    authFetch('/api/amr/clients')
      .then(res => res.json())
      .then(list => {
        setClients(list);
        if (list.length > 0) {
          setSelectedClient(list[0].id);
        }
      })
      .catch(err => console.error('Failed to fetch clients:', err));
  }, [isAuthenticated, authFetch]);

  // Fetch API key status on mount
  useEffect(() => {
    if (!isAuthenticated) return;
    authFetch('/api/amr/keys/status')
      .then(res => res.json())
      .then(data => setKeyStatus(data.keys || {}))
      .catch(err => console.error('Failed to fetch key status:', err));
  }, [isAuthenticated, authFetch]);

  // Save API keys handler
  const handleSaveKeys = useCallback(() => {
    const toSave = {};
    Object.entries(keyInputs).forEach(([plat, val]) => {
      if (val.trim().length > 5) toSave[plat] = val.trim();
    });
    if (Object.keys(toSave).length === 0) {
      setKeyMessage('Enter at least one API key to save.');
      return;
    }
    setKeySaving(true);
    setKeyMessage('');
    authFetch('/api/amr/keys', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ keys: toSave }),
    })
      .then(res => res.json())
      .then(result => {
        setKeySaving(false);
        if (result.ok) {
          setKeyStatus(result.keys || {});
          setKeyInputs({ chatgpt: '', perplexity: '', gemini: '', grok: '' });
          setKeyMessage(`Saved ${result.updated.join(', ')} keys!`);
          setTimeout(() => setKeyMessage(''), 5000);
        } else {
          setKeyMessage('Error: ' + (result.error || 'Unknown error'));
        }
      })
      .catch(err => {
        setKeySaving(false);
        setKeyMessage('Failed: ' + err.message);
      });
  }, [keyInputs, authFetch]);

  // Fetch data whenever selected client changes
  const fetchData = useCallback(() => {
    if (!selectedClient) return;
    setLoading(true);
    setError(null);
    authFetch(`/api/amr/data?client=${encodeURIComponent(selectedClient)}`)
      .then(res => res.json())
      .then(result => {
        setLoading(false);
        if (result.error) {
          setError(result.error);
          setData({ history: [], forecast: [], decay: [] });
        } else {
          setData({
            history: result.history || [],
            forecast: result.forecast || [],
            decay: result.decay || []
          });
        }
      })
      .catch(err => {
        setLoading(false);
        setError('Failed to fetch data');
        console.error('Error fetching data:', err);
      });
  }, [selectedClient, authFetch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch per-prompt results
  const fetchResults = useCallback(() => {
    if (!selectedClient) return;
    authFetch(`/api/amr/results?client=${encodeURIComponent(selectedClient)}`)
      .then(res => res.json())
      .then(data => setResults(data))
      .catch(err => console.error('Failed to fetch results:', err));
  }, [selectedClient, authFetch]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  // Run collection handler
  const handleCollect = useCallback(() => {
    if (collecting || !selectedClient) return;
    setCollecting(true);
    setCollectProgress('Starting collection...');

    // Safety timeout — reset after 3 minutes if no completion event arrives
    const safetyTimeout = setTimeout(() => {
      setCollecting(false);
      setCollectProgress('');
      fetchResults();
    }, 3 * 60 * 1000);

    authFetch('/api/amr/collect', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clientId: selectedClient }),
    })
      .then(res => res.json())
      .then(result => {
        if (result.error) {
          setCollectProgress(`Error: ${result.error}`);
          setCollecting(false);
          clearTimeout(safetyTimeout);
        } else {
          setCollectProgress(result.message || 'Collection started...');
        }
      })
      .catch(err => {
        setCollectProgress(`Failed: ${err.message}`);
        setCollecting(false);
        clearTimeout(safetyTimeout);
      });
  }, [collecting, selectedClient, authFetch, fetchResults]);

  // Listen for Socket.io collection progress
  useEffect(() => {
    const getSocket = () => window.__amrSocket;
    let socket = getSocket();

    // If socket isn't ready yet, poll for it
    let pollTimer;
    if (!socket) {
      pollTimer = setInterval(() => {
        socket = getSocket();
        if (socket) {
          clearInterval(pollTimer);
          attachListeners(socket);
        }
      }, 500);
    }

    function attachListeners(s) {
      s.on('amr:collect-progress', (data) => {
        setCollectProgress(data.text || '');
      });
      s.on('amr:alert', (data) => {
        if (data.title === 'Collection Complete' || data.type === 'success' || data.type === 'error') {
          setCollecting(false);
          setCollectProgress('');
          fetchData();
          fetchResults();
        }
      });
    }

    if (socket) attachListeners(socket);

    return () => {
      if (pollTimer) clearInterval(pollTimer);
      const s = getSocket();
      if (s) {
        s.off('amr:collect-progress');
        s.off('amr:alert');
      }
    };
  }, [fetchData, fetchResults]);

  // Handle config form submission
  const handleAddClient = (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    // Validate inputs
    if (!newClientName.trim() || !newKeywords.trim() || !newPlatforms.trim()) {
      setFormError('All fields are required.');
      return;
    }

    // Prepare data
    const keywordsArray = newKeywords.split(',').map(kw => kw.trim()).filter(kw => kw);
    const platformsArray = newPlatforms.split(',').map(pl => pl.trim()).filter(pl => pl);

    if (keywordsArray.length === 0 || platformsArray.length === 0) {
      setFormError('Please provide at least one keyword and one platform.');
      return;
    }

    const config = {
      client: newClientName.trim(),
      keywords: keywordsArray,
      platforms: platformsArray
    };

    // Check for duplicate client name or ID
    const newId = config.client.replace(/\s+/g, '_').replace(/[^A-Za-z0-9_-]/g, '');
    const nameExists = clients.some(c => c.name.toLowerCase() === config.client.toLowerCase());
    const idExists = clients.some(c => c.id.toLowerCase() === newId.toLowerCase());

    if (nameExists || idExists) {
      setFormError('A client with this name already exists.');
      return;
    }

    // Post new config
    authFetch('/api/amr/config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config)
    })
      .then(res => res.json())
      .then(result => {
        if (result.error) {
          setFormError('Error saving config: ' + result.error);
        } else {
          const newId = result.id;
          const newClientEntry = { id: newId, name: config.client };
          setClients(prev => [...prev, newClientEntry]);
          setSelectedClient(newId);
          setNewClientName('');
          setNewKeywords('');
          setNewPlatforms('');
          setFormSuccess(`Client "${config.client}" added successfully!`);
          setTimeout(() => setFormSuccess(''), 5000);
        }
      })
      .catch(err => {
        console.error('Failed to add config:', err);
        setFormError('Failed to add new client config.');
      });
  };

  // Calculate statistics
  const getStats = () => {
    if (data.history.length === 0) return null;

    const validHistory = data.history.filter(e => e.count !== null);
    if (validHistory.length === 0) return null;

    const total = validHistory.reduce((sum, e) => sum + e.count, 0);
    const average = (total / validHistory.length).toFixed(2);
    const max = Math.max(...validHistory.map(e => e.count));
    const latest = validHistory[validHistory.length - 1]?.count || 0;

    const trend = validHistory.length >= 2
      ? validHistory[validHistory.length - 1].count - validHistory[validHistory.length - 2].count
      : 0;

    return { total, average, max, latest, trend };
  };

  const stats = getStats();

  // If not authenticated, show login page
  if (!isAuthenticated) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <div className="app-container">
      {/* Real-time Alert Toasts */}
      <AlertToast alerts={alerts} onDismiss={dismissAlert} />

      {/* Header Section */}
      <div className="header">
        <div className="header-top">
          <div>
            <h1>AI Mention Rate Dashboard</h1>
            <p className="subtitle">Track your brand mentions across AI platforms in real-time</p>
          </div>
          <div className="header-actions">
            {connected && (
              <span className="badge badge-success live-badge">● Live</span>
            )}
            <button
              className={`btn btn-collect ${collecting ? 'collecting' : ''}`}
              onClick={handleCollect}
              disabled={collecting || !selectedClient}
              title={collecting ? 'Collection in progress...' : 'Run data collection for this client'}
            >
              {collecting ? '⏳ Collecting...' : '📡 Run Collection'}
            </button>
            <span className="user-badge">👤 {user?.username}</span>
            <button className="btn btn-logout" onClick={handleLogout}>Logout</button>
          </div>
        </div>

        <div className="client-selector">
          <label htmlFor="clientSelect">Select Client:</label>
          <select
            id="clientSelect"
            value={selectedClient}
            onChange={e => setSelectedClient(e.target.value)}
          >
            {clients.map(client => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
          {clients.length === 0 && (
            <span className="badge badge-warning">No clients yet - Add one below</span>
          )}
        </div>
      </div>

      {/* Main Content */}
      {
        selectedClient && (
          <>
            {/* Statistics Cards */}
            {stats && (
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-label">Total Mentions</div>
                  <div className="stat-value">{stats.total}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Average Rate</div>
                  <div className="stat-value">{stats.average}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Peak Mentions</div>
                  <div className="stat-value">{stats.max}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Latest Count</div>
                  <div className="stat-value">{stats.latest}</div>
                  {stats.trend !== 0 && (
                    <div className={`stat-change ${stats.trend > 0 ? 'positive' : 'negative'}`}>
                      {stats.trend > 0 ? '↑' : '↓'} {Math.abs(stats.trend)} from previous
                    </div>
                  )}
                </div>
                {/* Boost Button */}
                <div className="stat-card boost-stat-card" onClick={() => setShowBoostModal(true)}>
                  <div className="stat-label">Boost</div>
                  <div className="stat-value boost-icon">🚀</div>
                  {activeBoost && (
                    <div className="stat-change">Draft generated — not published</div>
                  )}
                </div>
              </div>
            )}

            <div className="content-wrapper">
              {/* Historical Data Card */}
              <div className="card">
                <h2>📊 Historical Mentions</h2>
                {loading ? (
                  <div className="loading">
                    <div className="loading-spinner"></div>
                    <p>Loading data...</p>
                  </div>
                ) : error ? (
                  <div className="error-message">
                    Error: {error}
                  </div>
                ) : data.history.length === 0 ? (
                  <div className="no-data">
                    <p>No historical data available yet.</p>
                    <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                      Run the orchestrator or tracker to generate data.
                    </p>
                  </div>
                ) : (
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={data.history.slice(-10)} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <defs>
                          <linearGradient id="histGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#667eea" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#764ba2" stopOpacity={0.05} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#666' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#666' }} />
                        <Tooltip
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e0e0e0', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                          formatter={(value) => [value, 'Mentions']}
                        />
                        <Line
                          type="monotone"
                          dataKey="count"
                          stroke="#667eea"
                          strokeWidth={3}
                          dot={{ fill: '#667eea', r: 4 }}
                          activeDot={{ r: 6, fill: '#764ba2' }}
                          animationDuration={500}
                          animationEasing="ease-in-out"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Forecast Card */}
              <div className="card">
                <h2>🔮 Predictive Forecast</h2>
                {loading ? (
                  <div className="loading">
                    <div className="loading-spinner"></div>
                    <p>Loading predictions...</p>
                  </div>
                ) : data.forecast.length === 0 ? (
                  <div className="no-data">
                    <p>No forecast data available.</p>
                    <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                      Run sim_predict.py to generate forecasts.
                    </p>
                  </div>
                ) : (
                  <>
                    <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.95rem' }}>
                      Next {data.forecast.length} days prediction using time series analysis
                    </p>
                    <div className="chart-container">
                      <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={data.forecast} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                          <defs>
                            <linearGradient id="forecastGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#764ba2" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#667eea" stopOpacity={0.05} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
                          <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#666' }} />
                          <YAxis tick={{ fontSize: 11, fill: '#666' }} />
                          <Tooltip
                            contentStyle={{ borderRadius: '8px', border: '1px solid #e0e0e0', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                            formatter={(value, name) => {
                              const label = name === 'predicted' ? 'Predicted' : name === 'ci_upper' ? 'Upper CI' : name === 'ci_lower' ? 'Lower CI' : name;
                              return [value, label];
                            }}
                          />
                          {/* CI ribbon */}
                          <Area type="monotone" dataKey="ci_upper" stroke="none" fill="url(#forecastGradient)" animationDuration={500} />
                          <Area type="monotone" dataKey="ci_lower" stroke="none" fill="white" animationDuration={500} />
                          <Area
                            type="monotone"
                            dataKey="predicted"
                            stroke="#764ba2"
                            strokeWidth={3}
                            fill="url(#forecastGradient)"
                            dot={{ fill: '#764ba2', r: 4 }}
                            animationDuration={500}
                            animationEasing="ease-in-out"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                )}
              </div>

              {/* Decay Scenario Card */}
              <div className="card">
                <h2>📉 Decay Scenario</h2>
                {loading ? (
                  <div className="loading">
                    <div className="loading-spinner"></div>
                    <p>Loading decay model...</p>
                  </div>
                ) : data.decay.length === 0 ? (
                  <div className="no-data">
                    <p>No decay simulation available.</p>
                    <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                      Run amr_decay_sim.py to model decay scenarios.
                    </p>
                  </div>
                ) : (
                  <>
                    <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.95rem' }}>
                      "No new mentions" scenario with exponential decay
                    </p>
                    <ul className="data-list">
                      {data.decay.map((entry, idx) => (
                        <li key={idx}>
                          <span className="date">{entry.date}</span>
                          <span className="value">{entry.count}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              {/* Interactive Decay Simulator — Spark 1 */}
              <DecaySimulator currentRate={stats?.latest || 50} />

              {/* Per-Prompt Results Card */}
              {results && results.results && results.results.length > 0 && (
                <div className="card results-card">
                  <div className="results-header">
                    <h2>🎯 Last Collection Results</h2>
                    {results.summary && (
                      <div className="amr-badge">
                        <span className="amr-rate">{results.summary.amrRate}%</span>
                        <span className="amr-label">AMR</span>
                      </div>
                    )}
                  </div>
                  {results.summary && (
                    <div className="results-summary">
                      <div className="results-stat">
                        <span className="results-stat-value">{results.summary.mentioned}</span>
                        <span className="results-stat-label">Mentioned</span>
                      </div>
                      <div className="results-stat">
                        <span className="results-stat-value">{results.summary.total}</span>
                        <span className="results-stat-label">Total Queries</span>
                      </div>
                      {Object.entries(results.summary.platforms || {}).map(([plat, stats]) => (
                        <div className="results-stat" key={plat}>
                          <span className="results-stat-value">
                            {stats.checked > 0 ? ((stats.mentioned / stats.checked) * 100).toFixed(0) : 0}%
                          </span>
                          <span className="results-stat-label">{plat}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {results.lastRun && (
                    <p className="results-timestamp">
                      Last run: {new Date(results.lastRun).toLocaleString()}
                    </p>
                  )}
                  <div className="results-table-wrapper">
                    <table className="results-table">
                      <thead>
                        <tr>
                          <th>Prompt</th>
                          <th>Platform</th>
                          <th>Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.results.map((r, idx) => (
                          <tr key={idx} className={r.mentioned ? 'mentioned' : ''}>
                            <td className="prompt-cell">{r.prompt}</td>
                            <td>
                              <span className={`platform-pill ${r.platform}`}>
                                {r.platform}
                              </span>
                            </td>
                            <td>
                              <span className={`result-badge ${r.mentioned ? 'yes' : 'no'}`}>
                                {r.mentioned ? '✅ Mentioned' : '—'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Boost Modal — Spark 3 */}
            {showBoostModal && (
              <BoostModal
                clientId={selectedClient}
                clientName={clients.find(c => c.id === selectedClient)?.name}
                authFetch={authFetch}
                onClose={() => setShowBoostModal(false)}
                onBoostComplete={(result) => {
                  // A generated draft is not a live or published boost; this
                  // only reminds the operator a draft awaits human review.
                  setActiveBoost(result);
                }}
              />
            )}
          </>
        )
      }

      {/* API Keys Settings */}
      <div className="card form-card api-keys-card">
        <div className="form-header api-keys-header" onClick={() => setShowKeySettings(!showKeySettings)}>
          <h2>🔑 API Keys</h2>
          <div className="api-keys-toggle">
            <div className="key-status-dots">
              {Object.entries(keyStatus).map(([plat, info]) => (
                <span
                  key={plat}
                  className={`key-dot ${info.configured ? 'configured' : 'missing'}`}
                  title={`${plat}: ${info.configured ? 'Configured' : 'Not set'}`}
                />
              ))}
            </div>
            <span className="toggle-arrow">{showKeySettings ? '▲' : '▼'}</span>
          </div>
        </div>

        {showKeySettings && (
          <div className="api-keys-body">
            <p className="form-description">
              Configure your API keys securely. Keys are saved to the server and never exposed in full.
            </p>
            {[{ id: 'chatgpt', label: 'ChatGPT (OpenAI)', icon: '🤖', hint: 'sk-proj-...' },
            { id: 'perplexity', label: 'Perplexity', icon: '🔍', hint: 'pplx-...' },
            { id: 'gemini', label: 'Gemini (Google)', icon: '💎', hint: 'AIza...' },
            { id: 'grok', label: 'Grok (xAI)', icon: '⚡', hint: 'xai-...' },
            ].map(platform => (
              <div className="api-key-row" key={platform.id}>
                <div className="api-key-info">
                  <span className="api-key-icon">{platform.icon}</span>
                  <div>
                    <span className="api-key-label">{platform.label}</span>
                    {keyStatus[platform.id]?.configured && (
                      <span className="api-key-masked">{keyStatus[platform.id].masked}</span>
                    )}
                  </div>
                  <span className={`api-key-status ${keyStatus[platform.id]?.configured ? 'active' : 'inactive'}`}>
                    {keyStatus[platform.id]?.configured ? '● Active' : '○ Not set'}
                  </span>
                </div>
                <input
                  type="password"
                  className="form-input api-key-input"
                  placeholder={keyStatus[platform.id]?.configured ? 'Enter new key to replace...' : platform.hint}
                  value={keyInputs[platform.id]}
                  onChange={e => setKeyInputs(prev => ({ ...prev, [platform.id]: e.target.value }))}
                  autoComplete="off"
                />
              </div>
            ))}

            {keyMessage && (
              <div className={`form-message ${keyMessage.startsWith('Error') || keyMessage.startsWith('Failed') ? 'form-error' : 'form-success'}`}>
                <span className="message-icon">{keyMessage.startsWith('Error') || keyMessage.startsWith('Failed') ? '⚠' : '✓'}</span>
                <span className="message-text">{keyMessage}</span>
              </div>
            )}

            <div className="form-actions">
              <button
                className="btn btn-primary"
                onClick={handleSaveKeys}
                disabled={keySaving}
              >
                {keySaving ? 'Saving...' : '💾 Save Keys'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Client Form */}
      <div className="card form-card">
        <div className="form-header">
          <h2>➕ Add New Client</h2>
          <p className="form-description">
            Set up a new client to track their brand mentions across AI platforms
          </p>
        </div>

        <form onSubmit={handleAddClient}>
          {/* Client Name Field */}
          <div className="form-group">
            <label className="form-label">
              <span className="label-text">1. Client Name</span>
              <span className="label-hint">What's the business or brand name?</span>
            </label>
            <input
              type="text"
              className="form-input"
              value={newClientName}
              onChange={e => setNewClientName(e.target.value)}
              placeholder="Example: Silver Snow Studios"
            />
            <div className="field-help">
              This is how the client will appear in your dashboard
            </div>
          </div>

          {/* Keywords Field */}
          <div className="form-group">
            <label className="form-label">
              <span className="label-text">2. Keywords to Track</span>
              <span className="label-hint">Separate each keyword with a comma</span>
            </label>
            <textarea
              className="form-input form-textarea"
              value={newKeywords}
              onChange={e => setNewKeywords(e.target.value)}
              placeholder="Example: Silver Snow Studios, luxury car film production, automotive cinematography Marbella"
              rows="3"
            />
            <div className="field-help">
              These are the terms we'll search for in AI responses. Add:
              <ul className="help-list">
                <li>✓ Exact business name</li>
                <li>✓ Services they offer</li>
                <li>✓ Location + specialty</li>
              </ul>
            </div>
          </div>

          {/* Platforms Field */}
          <div className="form-group">
            <label className="form-label">
              <span className="label-text">3. AI Platforms to Monitor</span>
              <span className="label-hint">Separate each platform with a comma</span>
            </label>
            <div className="platform-buttons">
              <button
                type="button"
                className={`platform-tag ${newPlatforms.includes('chatgpt') ? 'active' : ''}`}
                onClick={() => {
                  const platforms = newPlatforms.split(',').map(p => p.trim()).filter(p => p);
                  if (platforms.includes('chatgpt')) {
                    setNewPlatforms(platforms.filter(p => p !== 'chatgpt').join(', '));
                  } else {
                    setNewPlatforms([...platforms, 'chatgpt'].join(', '));
                  }
                }}
              >
                ChatGPT
              </button>
              <button
                type="button"
                className={`platform-tag ${newPlatforms.includes('perplexity') ? 'active' : ''}`}
                onClick={() => {
                  const platforms = newPlatforms.split(',').map(p => p.trim()).filter(p => p);
                  if (platforms.includes('perplexity')) {
                    setNewPlatforms(platforms.filter(p => p !== 'perplexity').join(', '));
                  } else {
                    setNewPlatforms([...platforms, 'perplexity'].join(', '));
                  }
                }}
              >
                Perplexity
              </button>
              <button
                type="button"
                className={`platform-tag ${newPlatforms.includes('gemini') ? 'active' : ''}`}
                onClick={() => {
                  const platforms = newPlatforms.split(',').map(p => p.trim()).filter(p => p);
                  if (platforms.includes('gemini')) {
                    setNewPlatforms(platforms.filter(p => p !== 'gemini').join(', '));
                  } else {
                    setNewPlatforms([...platforms, 'gemini'].join(', '));
                  }
                }}
              >
                Gemini
              </button>
              <button
                type="button"
                className={`platform-tag ${newPlatforms.includes('claude') ? 'active' : ''}`}
                onClick={() => {
                  const platforms = newPlatforms.split(',').map(p => p.trim()).filter(p => p);
                  if (platforms.includes('claude')) {
                    setNewPlatforms(platforms.filter(p => p !== 'claude').join(', '));
                  } else {
                    setNewPlatforms([...platforms, 'claude'].join(', '));
                  }
                }}
              >
                Claude
              </button>
              <button
                type="button"
                className={`platform-tag ${newPlatforms.includes('copilot') ? 'active' : ''}`}
                onClick={() => {
                  const platforms = newPlatforms.split(',').map(p => p.trim()).filter(p => p);
                  if (platforms.includes('copilot')) {
                    setNewPlatforms(platforms.filter(p => p !== 'copilot').join(', '));
                  } else {
                    setNewPlatforms([...platforms, 'copilot'].join(', '));
                  }
                }}
              >
                Copilot
              </button>
              <button
                type="button"
                className={`platform-tag ${newPlatforms.includes('grok') ? 'active' : ''}`}
                onClick={() => {
                  const platforms = newPlatforms.split(',').map(p => p.trim()).filter(p => p);
                  if (platforms.includes('grok')) {
                    setNewPlatforms(platforms.filter(p => p !== 'grok').join(', '));
                  } else {
                    setNewPlatforms([...platforms, 'grok'].join(', '));
                  }
                }}
              >
                Grok
              </button>
            </div>
            <input
              type="text"
              className="form-input"
              style={{ marginTop: '0.5rem' }}
              value={newPlatforms}
              onChange={e => setNewPlatforms(e.target.value)}
              placeholder="Or type custom platforms separated by commas"
            />
            <div className="field-help">
              Click buttons above or type platform names separated by commas
            </div>
          </div>

          {/* Error and Success Messages */}
          {formError && (
            <div className="form-message form-error">
              <span className="message-icon">⚠</span>
              <span className="message-text">{formError}</span>
            </div>
          )}
          {formSuccess && (
            <div className="form-message form-success">
              <span className="message-icon">✓</span>
              <span className="message-text">{formSuccess}</span>
            </div>
          )}

          {/* Submit Button */}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary btn-large">
              <span className="btn-icon">+</span>
              Create Client Configuration
            </button>
          </div>
        </form>

        {/* Quick Start Guide */}
        <div className="quick-start">
          <h3>Quick Start Guide</h3>
          <ol className="start-steps">
            <li>Fill in the client name above</li>
            <li>Add 3-5 keywords related to the brand</li>
            <li>Select which AI platforms to track</li>
            <li>Click "Create Client Configuration"</li>
            <li>Run the tracker to start collecting data!</li>
          </ol>
        </div>
      </div>
    </div >
  );
}

export default App;
